/**
 * "Paste announcement": paste the usual WhatsApp announcement -> editable preview -> Publish.
 * Parsing and publishing happen on the server (parseAnnouncementText / publishAnnouncement); nothing is
 * invented: fields the text didn't contain stay empty or are marked "please check".
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { call, callableMessage } from "../../services/callables";
import { ChannelShareButtons } from "../whatsapp/ChannelShareButtons";
import { ref as sref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../lib/firebase";
import AnnouncementImages from "./AnnouncementImages";

type Slot = "breakfast" | "lunch" | "evening_snacks" | "dinner";
interface Dish { name: string; includes: string[]; price: number | null; variants?: { name: string; price: number }[] }
interface Parsed {
  kitchenName: string | null; title: string | null; slot: Slot | null; dishes: Dish[]; description: string | null;
  deliveryStart: string | null; deliveryEnd: string | null; cutoff: string | null; cutoffAssumed: boolean;
  limited: boolean; limitedQty: number | null; phone: string | null; location: string | null;
  date: string; flags: { field: string; note: string }[]; original: string; parser: string;
}
interface KitchenOpt { id: string; name: string; status?: string }
interface PublishResult { kitchenName: string; date: string; slot: Slot; updatedExisting: boolean; shareLink: string; shareText: string; channelText: string; imageUrl?: string | null; images?: string[]; photoNotes?: string[]; publishedAs?: string }

const SLOTS: { v: Slot; l: string }[] = [
  { v: "breakfast", l: "Breakfast" }, { v: "lunch", l: "Lunch" }, { v: "evening_snacks", l: "Evening snacks" }, { v: "dinner", l: "Dinner" },
];

const flagFor = (p: Parsed | null, field: string) => p?.flags.filter((f) => f.field === field || f.field.startsWith(field + ".")) || [];

const Field: React.FC<{ label: string; warn?: string[]; children: React.ReactNode }> = ({ label, warn, children }) => (
  <label className="block">
    <span className="block text-xs font-bold text-gray-600 mb-1">{label}</span>
    {children}
    {warn?.map((w, i) => <span key={i} className="block text-xs text-amber-700 mt-1">⚠️ {w}</span>)}
  </label>
);

const input = "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-orange-500 focus:outline-none";

export const PasteAnnouncement: React.FC<{
  /** chef / owner dashboards pass the selected kitchen; admins may leave it empty and choose */
  kitchenId?: string;
  draftId?: string;
  onPublished?: (r: PublishResult) => void;
}> = ({ kitchenId, draftId, onPublished }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [kitchens, setKitchens] = useState<KitchenOpt[]>([]);
  const [kid, setKid] = useState<string>(kitchenId || "");
  const [result, setResult] = useState<PublishResult | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [useAsDishPhoto, setUseAsDishPhoto] = useState(false);
  const [uploading, setUploading] = useState(0);

  // same Storage upload as ImageUpload, under announcements/{kitchenId}/ (rules: kitchen staff / admins)
  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    if (!kid) { toast.error("Choose the kitchen first"); return; }
    const list = Array.from(files).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type) && f.size < 8 * 1024 * 1024).slice(0, 10 - images.length);
    if (list.length < files.length) toast.error("Only JPG/PNG/WebP photos under 8 MB, up to 10");
    setUploading((n) => n + list.length);
    for (const f of list) {
      try {
        const r = sref(storage, `announcements/${kid}/${Date.now()}_${f.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(-60)}`);
        await uploadBytesResumable(r, f, { contentType: f.type });
        const url = await getDownloadURL(r);
        setImages((prev) => [...prev, url].slice(0, 10));
      } catch (e) {
        toast.error("Photo upload failed");
      }
      setUploading((n) => n - 1);
    }
  };

  useEffect(() => { if (kitchenId) setKid(kitchenId); }, [kitchenId]);
  useEffect(() => {
    if (!draftId) return;
    setBusy(true);
    call<any>("getAnnouncementDraft", { draftId })
      .then((r) => { setImages(r.images || []); setUseAsDishPhoto(!!r.useAsDishPhoto); setParsed(r.parsed); setText(r.parsed?.original || ""); setKitchens(r.kitchens || []); if (!kitchenId && r.suggestedKitchenId) setKid(r.suggestedKitchenId); })
      .catch((e) => toast.error(callableMessage(e, "Couldn't open the draft")))
      .finally(() => setBusy(false));
  }, [draftId, kitchenId]);

  const read = async () => {
    if (text.trim().length < 10) { toast.error("Paste the announcement first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await call<any>("parseAnnouncementText", { text });
      setParsed(r.parsed); setKitchens(r.kitchens || []);
      if (!kitchenId) setKid(r.suggestedKitchenId || "");
    } catch (e) { toast.error(callableMessage(e, "Couldn't read the announcement")); }
    setBusy(false);
  };

  const set = (patch: Partial<Parsed>) => setParsed((p) => (p ? { ...p, ...patch } : p));
  const setDish = (i: number, patch: Partial<Dish>) => setParsed((p) => (p ? { ...p, dishes: p.dishes.map((d, n) => (n === i ? { ...d, ...patch } : d)) } : p));

  const problems = (): string[] => {
    if (!parsed) return [];
    const out: string[] = [];
    if (!kid) out.push("choose the kitchen");
    if (!parsed.dishes.length) out.push("add a dish");
    parsed.dishes.forEach((d) => { if (!d.name.trim()) out.push("dish name"); if (!(Number(d.price) > 0)) out.push(`price for ${d.name || "dish"}`); });
    if (!parsed.slot) out.push("meal slot");
    if (!parsed.cutoff) out.push("order cut-off");
    return out;
  };

  const publish = async () => {
    if (!parsed) return;
    const pr = problems();
    if (pr.length) { toast.error(`Please fill in: ${pr.join(", ")}`); return; }
    setBusy(true);
    try {
      const r = await call<PublishResult>("publishAnnouncement", { kitchenId: kid, parsed, draftId, images, useAsDishPhoto });
      setResult(r);
      toast.success(r.updatedExisting ? "Special updated" : "Published to the menu");
      onPublished?.(r);
    } catch (e) { toast.error(callableMessage(e, "Publish failed")); }
    setBusy(false);
  };

  const copy = async (s: string) => {
    try { await navigator.clipboard.writeText(s); toast.success("Copied"); } catch { toast.error("Copy failed; select the text and copy"); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <h3 className="font-bold text-lg">📋 Paste announcement</h3>
        <p className="text-sm text-gray-600">Paste the message you send to your WhatsApp group. We'll read the dish, price, delivery time and cut-off for you to check.</p>
        <textarea className={`${input} min-h-[160px]`} value={text} onChange={(e) => setText(e.target.value)} placeholder={"🌿 My Kitchen | Dinner Special\n🍛 Paneer Masala with 3 Phulkas\n₹200/- per plate\n🛵 Delivery: 7:30–8:30 PM\n⏰ Order by 5 PM"} />
        <button type="button" onClick={read} disabled={busy} className="w-full min-h-[48px] rounded-xl bg-orange-600 text-white font-bold disabled:opacity-60">
          {busy && !parsed ? "Reading…" : parsed ? "Read again" : "Read announcement"}
        </button>
      </div>

      {parsed && !result && (
        <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-4">
          <h3 className="font-bold">Check and edit</h3>
          {(kitchens.length > 1 || !kitchenId) && (
            <Field label="Kitchen">
              <select className={input} value={kid} onChange={(e) => setKid(e.target.value)}>
                <option value="">— choose —</option>
                {kitchens.map((k) => <option key={k.id} value={k.id}>{k.name}{k.status && k.status !== "active" ? ` (${k.status})` : ""}</option>)}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Meal slot" warn={flagFor(parsed, "slot").map((f) => f.note)}>
              <select className={input} value={parsed.slot || ""} onChange={(e) => set({ slot: (e.target.value || null) as Slot | null })}>
                <option value="">—</option>
                {SLOTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
            <Field label="Date" warn={flagFor(parsed, "date").map((f) => f.note)}>
              <input type="date" className={input} value={parsed.date} onChange={(e) => set({ date: e.target.value })} />
            </Field>
          </div>

          {parsed.dishes.map((d, i) => (
            <div key={i} className="rounded-xl bg-orange-50 p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-orange-800">Dish {parsed.dishes.length > 1 ? i + 1 : ""}</span>
                {parsed.dishes.length > 1 && <button type="button" className="text-xs text-red-600" onClick={() => set({ dishes: parsed.dishes.filter((_, n) => n !== i) })}>Remove</button>}
              </div>
              <Field label="Name"><input className={input} value={d.name} onChange={(e) => setDish(i, { name: e.target.value })} /></Field>
              <Field label="Comes with (comma separated)"><input className={input} value={d.includes.join(", ")} onChange={(e) => setDish(i, { includes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></Field>
              {d.variants && d.variants.length > 1 ? (
                <div className="text-sm text-gray-700">Options: {d.variants.map((v) => `${v.name} ₹${v.price}`).join(" / ")}</div>
              ) : (
                <Field label="Price per plate (₹)" warn={flagFor(parsed, `dish${i + 1}`).map((f) => f.note)}>
                  <input type="number" inputMode="numeric" className={input} value={d.price ?? ""} onChange={(e) => setDish(i, { price: e.target.value === "" ? null : Number(e.target.value) })} />
                </Field>
              )}
            </div>
          ))}
          <button type="button" className="text-sm font-bold text-orange-700" onClick={() => set({ dishes: [...parsed.dishes, { name: "", includes: [], price: null }] })}>+ Add another dish</button>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Delivery from" warn={flagFor(parsed, "delivery").map((f) => f.note)}>
              <input type="time" className={input} value={parsed.deliveryStart || ""} onChange={(e) => set({ deliveryStart: e.target.value || null })} />
            </Field>
            <Field label="Delivery till">
              <input type="time" className={input} value={parsed.deliveryEnd || ""} onChange={(e) => set({ deliveryEnd: e.target.value || null })} />
            </Field>
          </div>
          <Field label="Order cut-off" warn={parsed.cutoffAssumed ? ["Not in your message: suggested 2 h before delivery. Please confirm or change."] : flagFor(parsed, "cutoff").map((f) => f.note)}>
            <input type="time" className={`${input} ${parsed.cutoffAssumed ? "border-amber-500 bg-amber-50" : ""}`} value={parsed.cutoff || ""} onChange={(e) => set({ cutoff: e.target.value || null, cutoffAssumed: false })} />
          </Field>
          <div className="grid grid-cols-2 gap-3 items-end">
            <label className="flex items-center gap-2 min-h-[44px]">
              <input type="checkbox" checked={parsed.limited} onChange={(e) => set({ limited: e.target.checked })} className="w-5 h-5" />
              <span className="text-sm">Limited plates</span>
            </label>
            {parsed.limited && (
              <Field label="How many (optional)">
                <input type="number" inputMode="numeric" className={input} value={parsed.limitedQty ?? ""} onChange={(e) => set({ limitedQty: e.target.value === "" ? null : Number(e.target.value) })} />
              </Field>
            )}
          </div>
          <div className="space-y-2">
            <span className="block text-xs font-bold text-gray-600">Photos ({images.length})</span>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {images.map((u, i) => (
                  <div key={u} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100">
                    <img src={u} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <button type="button" aria-label="Remove photo" onClick={() => setImages(images.filter((_, n) => n !== i))}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white text-sm">✕</button>
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-white/90 rounded px-1">main</span>}
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center justify-center gap-2 min-h-[48px] rounded-xl border-2 border-dashed border-orange-300 text-orange-700 font-bold cursor-pointer">
              📷 {uploading ? `Uploading ${uploading}…` : "Add photos"}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            </label>
            {images.length > 0 && (
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="w-5 h-5 mt-0.5" checked={useAsDishPhoto} onChange={(e) => setUseAsDishPhoto(e.target.checked)} />
                <span>Use the first photo as the dish photo even if the dish already has one <span className="text-gray-500">(new dishes or dishes without a photo get it anyway)</span></span>
              </label>
            )}
          </div>
          <Field label="Description">
            <textarea className={`${input} min-h-[70px]`} value={parsed.description || ""} onChange={(e) => set({ description: e.target.value || null })} />
          </Field>
          <p className="text-xs text-gray-400">Read by: {parsed.parser === "rules" ? "rule-based reader" : parsed.parser}. Re-publishing the same kitchen, day and meal updates the same special.</p>
          <button type="button" onClick={publish} disabled={busy || uploading > 0} className="w-full min-h-[52px] rounded-xl bg-green-600 text-white font-bold text-lg disabled:opacity-60">
            {busy ? "Publishing…" : "Publish to menu"}
          </button>
          {problems().length > 0 && <p className="text-xs text-red-600">Still needed: {problems().join(", ")}</p>}
        </div>
      )}

      {result && (
        <div className="bg-green-50 rounded-2xl border border-green-200 p-4 space-y-3">
          <p className="font-bold text-green-800">✅ {result.updatedExisting ? "Updated" : "Published"} on {result.kitchenName}'s menu ({result.date}){result.publishedAs === "admin" ? " as admin" : ""}</p>
          <p className="text-sm text-gray-700">The home-page notice is live and ends after delivery.</p>
          {result.photoNotes?.length ? <p className="text-sm text-amber-700">📷 {result.photoNotes.join("; ")}</p> : null}
          <div className="space-y-2">
            <span className="text-xs font-bold text-gray-600">Share link (paste it in your society group):</span>
            <div className="text-sm bg-white rounded-lg border p-3 break-words">{result.shareText}</div>
            <div className="flex gap-2">
              <button type="button" className="flex-1 min-h-[44px] rounded-lg bg-white border font-bold" onClick={() => copy(result.shareText)}>Copy message</button>
              <button type="button" className="flex-1 min-h-[44px] rounded-lg bg-white border font-bold" onClick={() => copy(result.shareLink)}>Copy link</button>
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-green-200">
            <span className="text-xs font-bold text-gray-600">Share to the Ruchira WhatsApp Channel (nothing is posted automatically):</span>
            <pre className="text-sm bg-white rounded-lg border p-3 whitespace-pre-wrap break-words font-sans max-h-64 overflow-auto">{result.channelText}</pre>
            <AnnouncementImages images={result.images} />
            <ChannelShareButtons text={result.channelText} imageUrl={result.images?.[0] || result.imageUrl || null} />
          </div>
          <button type="button" className="text-sm font-bold text-orange-700" onClick={() => { setResult(null); setParsed(null); setText(""); setImages([]); setUseAsDishPhoto(false); }}>Paste another</button>
        </div>
      )}
    </div>
  );
};

export default PasteAnnouncement;
